import { Component, Input, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CollapseComponent,
	InfoGroupComponent,
	ItemComponent,
	ListDetailComponent,
	TagComponent,
	TagGroupComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { IFormOperation } from '../../../../../operations/models/operations.models';
import { LanguageService } from '../../../../../core/services/language.service';
import { ITextLocaleMap } from '../../../../models/workflows.models';
import {
	findFormFieldPreset,
	inferFormFieldPresetId,
} from '../../../../components/modal-workflow-form-step/form-field-presets';
import { readFormStepsFromConfig, readStepFieldsFromApi } from '../../../../utils/form-operation-contract.util';
import { resolveFormStepListTags } from '../../../../utils/form-step-list-tags.util';
import { resolveWorkflowLocaleText } from '../../../../utils/workflow-locale-text.util';
import { DeveloperCurlButtonComponent } from '../../../../../shared/components/developer-curl-button/developer-curl-button.component';

@Component({
	selector: 'app-form-operation',
	imports: [
		TranslatePipe,
		CollapseComponent,
		TileComponent,
		ItemComponent,
		InfoGroupComponent,
		TagComponent,
		TagGroupComponent,
		ListDetailComponent,
		DeveloperCurlButtonComponent,
	],
	templateUrl: './form-operation.component.html',
	styleUrl: './form-operation.component.scss',
})
export class FormOperationComponent {
	private languageService = inject(LanguageService);
	private translate = inject(TranslateService);

	@Input() operation?: IFormOperation;
	@Input() index: number = 1;
	@Input() profileUuid?: string;

	public operationCurlPath(): string {
		if (!this.profileUuid || !this.operation?.uuid) return '';
		return `/profile/${this.profileUuid}/operations/${this.operation.uuid}`;
	}

	public fieldTypeLabel(type: string): string {
		return `WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.FIELD_TYPE_${type.toUpperCase()}`;
	}

	public resolveStepText(value: string | ITextLocaleMap | null | undefined): string {
		return resolveWorkflowLocaleText(value, this.languageService.getLanguage());
	}

	public get formSteps(): Record<string, unknown>[] {
		return readFormStepsFromConfig(this.operation?.config as Record<string, unknown> | undefined);
	}

	public stepFields(step: Record<string, unknown>): Record<string, unknown>[] {
		return readStepFieldsFromApi(step);
	}

	public stepTitle(step: Record<string, unknown>): string {
		return this.resolveStepText(step['title'] as string | ITextLocaleMap);
	}

	public stepDescription(step: Record<string, unknown>): string {
		return this.resolveStepText(step['description'] as string | ITextLocaleMap);
	}

	public stepOrder(step: Record<string, unknown>): number {
		return Number(step['order'] ?? step['step_id'] ?? 0);
	}

	public stepTrackId(step: Record<string, unknown>): string | number {
		return (step['uuid'] ?? step['order'] ?? '') as string | number;
	}

	public fieldTrackId(field: Record<string, unknown>): string {
		return String(field['uuid'] ?? field['id'] ?? '');
	}

	public fieldLabel(field: Record<string, unknown>): string {
		return this.resolveFieldTitle(field) || String(field['name'] ?? '');
	}

	public fieldType(field: Record<string, unknown>): string {
		return String(field['type'] ?? '');
	}

	public resolveFieldTitle(field: Record<string, unknown>): string {
		return resolveWorkflowLocaleText(field['title'] as string | ITextLocaleMap, this.languageService.getLanguage());
	}

	/** Nombre del preset del paso (Figma `8980:251433` / listado `9238:44360`). */
	public resolvePresetTypeLabel(step: Record<string, unknown>): string {
		const fields = this.stepFields(step);
		if (!fields.length) {
			return '';
		}

		const presetId = inferFormFieldPresetId(fields[0], fields);
		const preset = findFormFieldPreset(presetId);
		return preset?.labelKey ? this.translate.instant(preset.labelKey) : '';
	}

	/** Etiquetas de estado del paso: Obligatorio y Cotejar (Figma `9238:44360`). */
	public getStepListTags(step: Record<string, unknown>) {
		return resolveFormStepListTags(this.stepFields(step), {
			mandatory: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.MANDATORY'),
			matchOcr: this.translate.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.MATCH_OCR_TAG'),
		});
	}
}
